/*
 * @Author: cjw 
 * @Date: 2021-09-07 09:53:17 
 * @Last Modified by: cjw
 * @Last Modified time: 2021-10-11 17:43:34
 */

/*
 * 提供数据选取功能（使用数据字典和数据绑定的定义产生）
 *
 * 提供以下接口
 * 1. getDataSource(row)    类型为 DataTable 的数据源，定义时可能是表名又或者是静态数据，该接口统一转换为 DataTable
 * 2. setValues(row, data)  根据字段关系和填值字段自动填值（出现数据绑定字段时根据关系填值，出现当前表字段时直接填值）
 * 3. display_field         显示字段（用于下拉选择组件，需要设置显示字段）
 * 4. value_field           值字段（用于下拉选择组件，需要设置值字段）
 * 5. options               数据绑定选项（取自 bindings 对象中的 options）
*/

import { DataSet } from "../data-set"
import serviceProvider from "../../services/service-provider"

// 是否对象
function isObject(obj) {
    return typeof obj === 'object' && obj !== null
}

function getProxy(target, meta) {
    // 如果对象已有代理，则返回代理
    let observed = meta.toProxy.get(target)
    if (observed !== void 0) {
        return observed
    }

    if (meta.toRaw.get(target)) {
        return target
    }
}

// 返回目标字段对应的来源字段
function getSourceFieldName(dataBinding, targetFieldName) {
    const fieldRelation = dataBinding?.relation_fields?.find(relation => relation.target === targetFieldName);
    if (fieldRelation) {
        return fieldRelation.source;
    } else {
        return targetFieldName;
    }
}

// 返回来源字段对应的目标字段
function getTargetFieldName(dataBinding, sourceFieldName) {
    const fieldRelation = dataBinding?.relation_fields?.find(relation => relation.source === sourceFieldName);
    if (fieldRelation) {
        return fieldRelation.target;
    } else {
        return sourceFieldName;
    }
}

async function getConditions(binding, dataBinding, curRow, queryCondition) {
    const body = {}

    // 生成查询参数
    const query = {}
    // 设置了允许过滤的字段，根据当前行生成（优先级低）
    binding?.columns?.filters?.forEach(sourceFieldName => {
        // filters 是选择数据的字段名，需要转换为当前表的字段名，再取当前表的数据生成条件
        const targetFieldName = getTargetFieldName(dataBinding, sourceFieldName);
        if (targetFieldName && curRow[targetFieldName]) {
            query[sourceFieldName] = curRow[targetFieldName]
        }
    })
    // 用户传入的查询条件，用于一些特殊应用，作用是将条件传到后台接口（优先级中）
    if (queryCondition) {
        Object.assign(query, queryCondition)
    }
    // 数据绑定的固定条件（优先级高，因为在数据绑定中设置的固定条件它并不是默认值，而是要定必须按此条件过滤）
    const conditions = binding?.columns?.conditions
    if (conditions) {
        for (const key in conditions) {
            query[key] = conditions[key];
        }
    }

    // 由于数据源的参数有两种，路径参数和查询参数，前面生成的条件需要分离出路径参数和查询条件
    const dictionary = await serviceProvider.dataService.getDictionary(binding.data_source)
    if (dictionary.params?.length) {
        const params = {}
        for (let i = 0; i < dictionary.params.length; i++) {
            const param = dictionary.params[i];
            if (query[param.name]) {
                params[param.name] = query[param.name]
                delete query[param.name]
            } else {
                const targetFieldName = getTargetFieldName(dataBinding, param.name);
                if (targetFieldName && curRow[targetFieldName]) {
                    params[param.name] = curRow[targetFieldName]
                } else {
                    throw { message: `数据绑定中的数据源 ${binding.data_source} 需要路径参数 ${param.name}，但并未提供该参数` }
                }
            }
        }
        body.params = params
    }

    body.query = query

    // 只取需要的字段（启用字段+显示字段+填入字段）
    const fields = [];
    binding?.columns?.enableds?.forEach(fieldName => fields.push(fieldName))
    binding?.columns?.displays?.forEach(fieldName => {
        if (fields.indexOf(fieldName) === -1) {
            fields.push(fieldName);
        }
    })
    dataBinding?.value_fields?.forEach(fieldName => {
        if (fields.indexOf(fieldName) === -1) {
            fields.push(fieldName);
        }
    });
    body['fields'] = fields.join(',')
    return body;
}

// 获取数据源（根据当前行数据，结合数据字典绑定配置获取数据源，target 是列对象）
function createGetDataSource(target) {
    return async function getDataSource(curRow, queryCondition) {
        if (target.data_binding) {
            const dataSet = new DataSet()
            let dataTable
            let columns = []
            let datas = []
            switch (target.data_binding.type) {
                // 静态数据
                case 'static':
                    // 为了统计接口，将静态数据封装成 DataTable 对象再返回
                    columns = target.data_binding.static_columns.map(el => {
                        return {
                            field_name: el.field_name,
                            caption: el.caption,
                            visible: el.visible
                        }
                    })
                    // 深复制，防止被误修改
                    datas = target.data_binding.static_datas.map(el => {
                        const data = {}
                        target.data_binding.static_columns.forEach(column => {
                            data[column.field_name] = el[column.field_name]
                        })
                        return data
                    })
                    
                    dataTable = dataSet.createTable('binding_data_source', { datas, columns })
                    break;
                case 'binding': {
                    // 非数组时认为是绑定编号
                    if (!serviceProvider.dataService) {
                        throw '未提供 dataService 服务，数据源无法获取数据'
                    }
                    const binding = await serviceProvider.dataService.getBinding(target.data_binding.binding_name)
                    const condition = await getConditions(binding, target.data_binding, curRow, queryCondition)
                    dataTable = dataSet.createTable('binding_data_source', { datas, columns })
                    await serviceProvider.dataService.loadDataTable(binding.data_source, dataTable, condition)
                    break;
                }
                default:
                    break;
            }

            return dataTable
        } else {
            return undefined
        }
    }
}

// 批量赋值（根据字段关系和填值字段自动填值，出现数据绑定字段时根据关系填值，出现当前表字段时直接填值，target 是列对象）
function createSetValues(target) {
    return function setValues(curRow, data) {
        if (data) {
            // sourceFieldName 是选取数据源的字段名，targetFieldName 是当前表的字段名
            target?.data_binding?.value_fields?.forEach(sourceFieldName => {
                const targetFieldName = getTargetFieldName(target.data_binding, sourceFieldName)
                curRow[targetFieldName] = data[sourceFieldName]
            })
        } else {
            throw `setValues 参数 data 不能为空`
        }
    }
}

function createReactiveBinding(target, meta) {
    // 如果对象已有代理，则返回代理
    const proxyObject = getProxy(target, meta)
    if (proxyObject) {
        return proxyObject
    }

    // 创建 proxy 的 get 处理
    const createGetter = function() {
        return function get(target, property, receiver) {

            switch (property) {
                case 'binding': {
                    let result

                    if (target.data_binding) {
                        let displayField = ''
                        let valueField =''
                        // 只要使用当前字段获取对应的来源字段（即数据绑定的字段名称）
                        const sourceFieldName = getSourceFieldName(target.data_binding, target.field_name)
                        // 使用当前绑定字段作为显示字段（不建议使用 lookup 方式，这种方式数据使用ID或编号，显示时使用名称，会造成加载数据时需要将键值数据加载完才能显示）
                        displayField = sourceFieldName
                        // 填值字段需要判断当前字段是允许填入时才行
                        if (target.data_binding.value_fields.indexOf(sourceFieldName) !== -1) {
                            valueField = getSourceFieldName(target.data_binding, target.field_name)
                        }

                        result = {
                            getDataSource: createGetDataSource(target),
                            setValues: createSetValues(target),
                            display_field: displayField,
                            value_field: valueField,
                            options: {}
                        }
                    } 

                    return result
                }
                default:
                    break;
            }
            
            return Reflect.get(target, property, receiver)
        }
    }

    const handlers = {
        get: createGetter(meta)
    }

    // 创建 Proxy 代理
    const observed = new Proxy(target, handlers)
    meta.toProxy.set(target, observed)
    meta.toRaw.set(observed, target)
    return observed
}

function createReactiveColumns(target, meta) {
    // 如果对象已有代理，则返回代理
    const proxyObject = getProxy(target, meta)
    if (proxyObject) {
        return proxyObject
    }

    // 创建 proxy 的 get 处理
    const createGetter = function() {
        return function get(target, property /*, receiver */) {
            // 不能传递 receiver 参数，如果传入 DataTable 的 datas 用 vue 的 reactive 封装后会出现死循环，不用 reactive 则界面不刷新
            const res = Reflect.get(target, property/*, receiver*/)
            // 如果是对象，递归代理
            return isObject(res) ? createReactiveBinding(res, meta) : res
        }
    }

    const handlers = {
        get: createGetter(meta)
    }

    // 创建 Proxy 代理
    const observed = new Proxy(target, handlers)
    meta.toProxy.set(target, observed)
    meta.toRaw.set(observed, target)
    return observed
}

function createReactiveDataSource(target, meta) {
    const createGetter = function() {
        return function get(target, property, receiver) {

            switch (property) {
                case 'columns': {
                    const columns = Reflect.get(target, property, receiver)
                    return createReactiveColumns(columns, meta)
                }
                default:
                    break;
            }
            
            return Reflect.get(target, property, receiver)
        }
    }

    const handlers = {
        get: createGetter()
    }

    return new Proxy(target, handlers)
}

export function ProxyBinding(target, options) {
    const meta = {
        options: options,
        // 已代理的 columns 集合（key:原始对象，value:代理对象），用于解决重复代理
        toProxy: new WeakMap(),
        // 已代理的 columns 集合（key:代理对象，value:原始对象），与 toProxy 相反
        toRaw: new WeakMap()
    }

    return createReactiveDataSource(target, meta)
}